import React, { useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import axios from 'axios';

const EnergyManagement = () => {
  const [gaugeData, setGaugeData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Define gauge options for different metrics with appropriate ranges
  const getGaugeOptions = (metric) => {
    switch(metric) {
      case 'energy':
        return {
          width: 250,
          height: 250,
          greenFrom: 0,
          greenTo: 200,
          yellowFrom: 200,
          yellowTo: 400,
          redFrom: 400,
          redTo: 500,
          minorTicks: 10,
          majorTicks: [0, 100, 200, 300, 400, 500],
          min: 0,
          max: 500,
        };
      case 'current':
        return {
          width: 250,
          height: 250,
          greenFrom: 0,
          greenTo: 60,
          yellowFrom: 60,
          yellowTo: 80,
          redFrom: 80,
          redTo: 100,
          minorTicks: 5,
          majorTicks: [0, 20, 40, 60, 80, 100],
          min: 0,
          max: 100,
        };
      case 'power':
        return {
          width: 250,
          height: 250,
          greenFrom: 0,
          greenTo: 30,
          yellowFrom: 30,
          yellowTo: 45,
          redFrom: 45,
          redTo: 60,
          minorTicks: 5,
          majorTicks: [0, 10, 20, 30, 40, 50, 60],
          min: 0,
          max: 60,
        };
      case 'voltage':
        return {
          width: 250,
          height: 250,
          greenFrom: 380,
          greenTo: 420,
          yellowFrom: 360,
          yellowTo: 380,
          redFrom: 420,
          redTo: 440,
          minorTicks: 5,
          majorTicks: [320, 360, 380, 400, 420, 440, 460],
          min: 320,
          max: 460,
        };
      case 'frequency':
        return {
          width: 250,
          height: 250,
          greenFrom: 49.5,
          greenTo: 50.5,
          yellowFrom: 48,
          yellowTo: 49.5,
          redFrom: 50.5,
          redTo: 52,
          minorTicks: 5,
          majorTicks: [48, 49, 50, 51, 52],
          min: 48,
          max: 52,
        };
      default:
        return {
          width: 250,
          height: 250,
          greenFrom: 0,
          greenTo: 200,
          yellowFrom: 200,
          yellowTo: 400,
          redFrom: 400,
          redTo: 500,
          minorTicks: 10,
          majorTicks: [0, 100, 200, 300, 400, 500],
          min: 0,
          max: 500,
        };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://cmti-edge.online/foundry/php/EnergyMngt.php');
        console.log("API Response:", response.data); // Add this for debugging
        
        // Check response structure - PHP wraps data in a 'data' property
        const apiData = response.data.data || response.data;
        
        // Check if the response contains an error
        if (apiData.error) {
          setError(apiData.error);
          setLoading(false);
          return;
        }
        
        setGaugeData({
          // Printer Unit (energy_meter_1)
          energyGaugeprntEnergyGauge: apiData.Em1_Wh_Received / 1000,
          energyGaugeprntCurrentGauge: parseFloat(apiData.Em1_Current_Total),
          energyGaugeprntPowerGauge: apiData.Em1_Watts_Total / 1000,
          energyGaugeprntVoltageGauge: parseFloat(apiData.Em1_VLL_Avg),
          energyGaugeprntFrequencyGauge: parseFloat(apiData.Em1_PF),
    
          // Panel Unit (energy_meter_2)
          panelEnergyGauge: apiData.Em2_Wh_Received / 1000,
          panelCurrentGauge: parseFloat(apiData.Em2_Current_Total),
          panelPowerGauge: apiData.Em2_Watts_Total / 1000,
          panelVoltageGauge: parseFloat(apiData.Em2_VLL_Avg),
          panelFrequencyGauge: parseFloat(apiData.Em2_PF),
    
          // Furnace Unit (energy_meter_3)
          FurnaceEnergyGauge: apiData.Em3_Wh_Received / 1000,
          FurnaceCurrentGauge: parseFloat(apiData.Em3_Current_Total),
          FurnacePowerGauge: apiData.Em3_Watts_Total / 1000,
          FurnaceVoltageGauge: parseFloat(apiData.Em3_VLL_Avg),
          FurnaceFrequencyGauge: parseFloat(apiData.Em3_PF),
    
          // Moulding Unit (energy_meter_4)
          MouldEnergyGauge: apiData.Em4_Wh_Received / 1000,
          MouldCurrentGauge: parseFloat(apiData.Em4_Current_Total),
          MouldPowerGauge: apiData.Em4_Watts_Total / 1000,
          MouldVoltageGauge: parseFloat(apiData.Em4_VLL_Avg),
          MouldFrequencyGauge: parseFloat(apiData.Em4_PF),
        });
        
        setLastUpdate(new Date().toLocaleTimeString());
        setError(null);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data. Please check your connection.');
        setLoading(false);
      }
    };

    fetchData();
    
    // Real-time data polling every 10 seconds
    const interval = setInterval(fetchData, 10000);
    
    // Clean up on component unmount
    return () => clearInterval(interval);
  }, []);

  const renderGauges = (unit) => {
    const gaugeMetrics = [
      { id: 'Energy', label: 'ENERGY (kWh)', type: 'energy' },
      { id: 'Current', label: 'CURRENT (A)', type: 'current' },
      { id: 'Power', label: 'POWER (kW)', type: 'power' },
      { id: 'Voltage', label: 'VOLTAGE (V)', type: 'voltage' },
      { id: 'Frequency', label: 'POWER FACTOR ', type: 'frequency' },
    ];

    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 xl:grid-cols-5">
        {gaugeMetrics.map((gauge) => {
          const gaugeId = `${unit}${gauge.id}Gauge`;
          return (
            <div key={gaugeId} className="flex flex-col bg-gray-800 rounded-lg items-center py-7">
              <h6 className="text-center text-white text-xs font-bold lg:text-lg md:text-base mt-2 sm:text-sm">{gauge.label}</h6>
              <div className="flex h-72 justify-center w-full items-center overflow-hidden">
                <Chart
                  chartType="Gauge"
                  className="flex justify-center items-center"
                  data={[['Label', 'Value'], ['', gaugeData[gaugeId] || 0]]}
                  options={getGaugeOptions(gauge.type)}
                />
              </div>
              <div className="text-center text-white text-xs mt-2">
                Value: {gaugeData[gaugeId]?.toFixed(2) || 'N/A'} {gauge.label.split(' ')[1]}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id="energy" className="bg-gray-900 p-4">
      {/* Header with last update time */}
      <div className="flex bg-gray-800 justify-between rounded-lg items-center mb-4 px-4 py-3">
        <h1 className="text-3xl text-teal-400 font-bold">Energy Management System</h1>
        <div className="text-white">
          {loading ? (
            <span className="animate-pulse">Updating...</span>
          ) : (
            <span>Last updated: {lastUpdate}</span>
          )}
        </div>
      </div>
      
      {/* Error message if any */}
      {error && (
        <div className="bg-red-600 p-4 rounded-lg text-white mb-4">
          {error}
        </div>
      )}

      {/* Printer Unit */}
      <div className="bg-gray-800 rounded-lg text-2xl text-center text-teal-400 font-bold mb-4 py-2">PRINTER UNIT</div>
      {renderGauges('energyGaugeprnt')}

      {/* Panel Unit */}
      <div className="bg-gray-800 rounded-lg text-2xl text-center text-teal-400 font-bold mb-4 mt-4 py-2">PANEL UNIT</div>
      {renderGauges('panel')}

      {/* Furnace Unit */}
      <div className="bg-gray-800 rounded-lg text-2xl text-center text-teal-400 font-bold mb-4 mt-4 py-2">FURNACE UNIT</div>
      {renderGauges('Furnace')}

      {/* Moulding Unit */}
      <div className="bg-gray-800 rounded-lg text-2xl text-center text-teal-400 font-bold mb-4 mt-4 py-2">MOULDING UNIT</div>
      {renderGauges('Mould')}
      
      {/* Footer with refresh button */}
      <div className="flex justify-center mt-6">
        <button 
          onClick={() => window.location.reload()}
          className="bg-teal-500 rounded text-white font-bold hover:bg-teal-600 px-4 py-2"
        >
          Refresh All Data
        </button>
      </div>
    </div>
  );
};

export default EnergyManagement;